import {CustomerServiceSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/basket_sections.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  Paper,
  Text,
  UnstyledLink,
} from '@aboutyou/ui-components';
import {isCaseTypeCheck} from '@aboutyou/utils';
import React from 'react';
import styled, {css} from 'styled-components';
import {useCustomerServiceTranslations} from '../../../features/HelpCenter/useCustomerServiceTranslations';
import {useHelpCenterChatbot} from '../../HelpCenterChatbot/hooks/useHelpCenterChatbot';
import {useHelpCenterChatbotActive} from '../../HelpCenterChatbot/hooks/useHelpCenterChatbotActive';

export const CustomerServiceView = ({type}: CustomerServiceSection) => {
  const {t} = useTranslation();
  const customerServiceTranslations = useCustomerServiceTranslations();
  const {isActive: helpCenterChatbotActive} = useHelpCenterChatbotActive();
  const {open: openHelpCenterChatbot, isOpening: isHelpCenterChatbotOpening} =
    useHelpCenterChatbot();

  if (!isCaseTypeCheck('contactInfo', type) || !type.contactInfo) {
    return null;
  }

  const {phone, email, whatsapp} = type.contactInfo;

  if (!phone && !email && !whatsapp) {
    return null;
  }

  return (
    <StyledPaper>
      <ContactWrapper data-testid="customerServiceContainer">
        {!whatsapp && (
          <ContactTitle>
            <Text size="px16" weight="bold">
              {t('Customer Service')}
            </Text>
          </ContactTitle>
        )}

        {helpCenterChatbotActive && (
          <>
            <ContactButton
              onClick={() => {
                openHelpCenterChatbot();
              }}
              data-testid="contact"
            >
              <TextWithIcon data-testid="serviceContact">
                <Icon asset={GenericIconName.Help} size="S" />
                {customerServiceTranslations.chat_with_us}
              </TextWithIcon>

              <TextWithLineBreaks data-testid="serviceText">
                {customerServiceTranslations.get_instant_help}
              </TextWithLineBreaks>

              <Button
                variant="secondary"
                isLoading={isHelpCenterChatbotOpening}
                aria-label={undefined}
              >
                {customerServiceTranslations.start_chat}
              </Button>
            </ContactButton>
          </>
        )}

        {!helpCenterChatbotActive && email && (
          <ContactLink
            to={email.href}
            data-testid="contact"
            aria-label={undefined}
          >
            <TextWithIcon data-testid="serviceContact">
              <Icon asset={GenericIconName.MailBlack} size="S" />
              {email.label}
            </TextWithIcon>

            <TextWithLineBreaks data-testid="serviceText">
              {email.description}
            </TextWithLineBreaks>
          </ContactLink>
        )}

        {whatsapp && (
          <ContactLink
            to={whatsapp.href}
            data-testid="contact"
            aria-label={undefined}
          >
            <TextWithIcon data-testid="serviceContact">
              <Icon asset={GenericIconName.SocialWhatsapp} size="S" />
              {whatsapp.label}
            </TextWithIcon>

            <TextWithLineBreaks data-testid="serviceText">
              {whatsapp.description}
            </TextWithLineBreaks>
          </ContactLink>
        )}

        {phone && (
          <ContactLink
            to={phone.href}
            data-testid="contact"
            aria-label={undefined}
          >
            <TextWithIcon data-testid="serviceContact">
              <Icon asset={GenericIconName.Phone} size="S" />
              {customerServiceTranslations.give_us_a_call}
            </TextWithIcon>

            <TextWithLineBreaks data-testid="serviceText">
              {phone.description}
            </TextWithLineBreaks>

            <TextWithIcon>{phone.label}</TextWithIcon>
          </ContactLink>
        )}
      </ContactWrapper>
    </StyledPaper>
  );
};

const StyledPaper = styled(Paper)`
  border-top: none;
  border-top-left-radius: unset;
  border-top-right-radius: unset;
`;

const ContactWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 32px;
  padding-top: 8px;
`;

const contactStyles = css`
  gap: 16px;
  display: flex;
  flex-direction: column;
  width: 33%;
`;

const ContactButton = styled.span`
  ${contactStyles}
  text-align: left;
  cursor: pointer;
`;

const ContactLink = styled(UnstyledLink)`
  ${contactStyles}
`;

const ContactTitle = styled.div`
  flex-basis: 50%;
`;

const TextWithIcon = styled(Text)`
  display: flex;
  align-items: center;
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 20px;

  & > svg {
    margin-right: 6px;
  }
`;

const TextWithLineBreaks = styled(Text)`
  word-break: break-word;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 20px;
  color: ${COLORS.Grey1};
`;
